import { useEffect } from 'react'
import Script from 'next/script'

import { posthog } from 'posthog-js'

import {
  setCookieConsentApplicable,
  selectCookieConsentStatus,
  selectShowCookieConsentBanner,
} from '~/redux/slices/user'
import { useGetUserLocationInfoFromIpQuery } from '~/redux/apis/geo-location-api'
import { EEA_COUNTRY_CODE } from '~/shared/constants/consantApplicableRegions.constant'
import AnalyticsEvents_V2 from '~/analytics/analyticsGA4'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import useGetExpectedLanguage from '~/curriculum/hooks/useGetExpectedLanguage'
import { AppLanguage } from '~/types/locale'

// This MUST match the languages configured in cookieinformation - setting an unsupported language makes the cookie dialog fail silently
// Prod config: https://go.cookieinformation.com/consent-solutions/56477/copy-and-translations
// Dev config: https://go.cookieinformation.com/consent-solutions/66188/copy-and-translations
const SUPPORTED_LANGUAGES = ['EN', 'NO'] as const
const AppLanguageToSupportedLanguageMap: { [K in AppLanguage]: (typeof SUPPORTED_LANGUAGES)[number] } = {
  en: 'EN',
  no: 'NO',
  uk: 'EN',
} as const
const DEFAULT_LANGUAGE = 'EN' as const

export const CookieConsentScript = () => {
  const loadConsentBanner = useAppSelector(selectCookieConsentStatus)
  const showConsentBanner = useAppSelector(selectShowCookieConsentBanner)
  const dispatch = useAppDispatch()
  const { isSuccess, data, isError } = useGetUserLocationInfoFromIpQuery()
  const expectedLanguage = useGetExpectedLanguage()

  useEffect(() => {
    if (isSuccess) {
      const { countryCode } = data
      const isEEA = EEA_COUNTRY_CODE.includes(countryCode as (typeof EEA_COUNTRY_CODE)[number])
      const isConsentApplicable = isEEA || showConsentBanner

      dispatch(setCookieConsentApplicable(isConsentApplicable))

      if (!isConsentApplicable) {
        posthog.opt_in_capturing()
        AnalyticsEvents_V2.grantAllConsent()
        AnalyticsEvents_V2.setConsentStatus(true)
      }
    }

    if (isError) {
      dispatch(setCookieConsentApplicable(true))
      posthog.opt_in_capturing()
      AnalyticsEvents_V2.grantAllConsent()
      AnalyticsEvents_V2.setConsentStatus(true)
    }
  }, [isSuccess, data, dispatch, isError, showConsentBanner])

  const culture = expectedLanguage
    ? AppLanguageToSupportedLanguageMap[expectedLanguage] ?? DEFAULT_LANGUAGE
    : DEFAULT_LANGUAGE

  if (!loadConsentBanner && !showConsentBanner) {
    return
  }

  return (
    <Script
      data-culture={culture}
      data-gcm-enabled="false"
      data-gcm-version="2.0"
      id="CookieConsent"
      src="https://policy.app.cookieinformation.com/uc.js"
      type="text/javascript"
    />
  )
}
